import React from "react";
import { cn } from "../../../lib/cn";
import { Badge } from "./Badge";
import { Tooltip } from "./Tooltip";

/**
 * Tabs — ui-v2.5. Navegación entre secciones hermanas. Subrayado inferior (no pastilla).
 * Especificación: ui-v2.5-catalogo-componentes.html · sección Tabs.
 * NO es el patrón de ui-v2. CONTROLADO: recibe `value` + `onChange`.
 *
 * <Tabs value={t} onChange={setT}>
 *   <Tab value="all" label="Todas" count={7} />
 *   <Tab value="int" label="Internos" />
 *   <TabSeparator />
 *   <Tab value="doc" label="Documentos" disabled tooltip="Disponible en próximas fases" />
 * </Tabs>
 */

export function Tab({ label, count, disabled, tooltip, active, onSelect }) {
  const btn = (
    <button
      type="button"
      role="tab"
      aria-selected={!!active}
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        "flex items-center gap-1 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm transition-colors",
        "focus-visible:outline-none focus-visible:border-v25-focus focus-visible:shadow-v25-focus",
        disabled
          // pointer-events-none: deja pasar el hover al <span> del Tooltip envolvente (como el Button de ui-v2)
          ? "pointer-events-none cursor-not-allowed border-transparent text-v25-ink-300"
          : active
          ? "border-v25-brand-800 font-semibold text-v25-brand-800"
          : "border-transparent text-v25-ink-500 hover:text-v25-ink-700"
      )}
    >
      {label}
      {count != null && <Badge tone="info" className="ml-1">{count}</Badge>}
    </button>
  );
  // Deshabilitada con motivo → Tooltip envolvente (funciona sobre el botón disabled).
  return disabled && tooltip ? <Tooltip content={tooltip}>{btn}</Tooltip> : btn;
}

export function TabSeparator() {
  return <div className="mx-1.5 h-5 w-px shrink-0 bg-v25-line" aria-hidden="true" />;
}

export function Tabs({ value, onChange, children, className }) {
  return (
    <div role="tablist" className={cn("flex items-center gap-1 overflow-x-auto border-b border-v25-line", className)}>
      {React.Children.map(children, (child) => {
        if (!React.isValidElement(child) || child.type !== Tab) return child;
        return React.cloneElement(child, {
          active: child.props.value === value,
          onSelect: () => onChange?.(child.props.value),
        });
      })}
    </div>
  );
}
